import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Button, SelectChangeEvent, Stack, Typography } from '@mui/material';
import { useFormik } from 'formik';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { getHydraulicConductivity, getLayersInSeries } from '../../../calculation';
import { hydraulicConductivity, layersInSeries, permeabilityChangeUponRemodeling, remodeledSoilPermeability, soilInputDataPD, soilPD, soilType, thickness } from '../../../conversion/soil';
import inputRangeSoil from '../../../inputRange/soil';
import { getLanguage } from '../../../localization';
import { cleanImage } from '../../../store/Image/imageSlice';
import { cleanAlert } from '../../../store/alert/alertSlice';
import { useAppDispatch, useAppSelector } from '../../../store/hook';
import { selectProject, setSoilInputData } from '../../../store/project/projectSlice';
import { cleanResult } from '../../../store/result/resultSlice';
import Input from '../../component/Input';
import Switch from '../../component/Switch';
import { EUnitOfMeasure } from '../../enum';
import { usePrevious } from '../../hook/hook';
import { IInputSoil, ISoilInputData, TInputSoil, TSoilInputData } from '../../iProject';
import { dialog } from '../../util';
import { getInputFirstLayerSoil, getInputNextLayerSoil } from '../util';
import ModalSoil from './ModalSoil';

interface IInputSoilProps {}

const layerNumber = 'layerNumber';

const InputSoil = (props: IInputSoilProps) => {
    const project = useAppSelector(selectProject);
    const dispatch = useAppDispatch();

    const { t } = useTranslation();

    const [openModal, setOpenModal] = React.useState(false);
    const [indexSelected, setIndexSelected] = React.useState(0);

    let iso = getLanguage();
    let prevIso = usePrevious(iso);

    let prevSoils = usePrevious(project.data!.soilInputData!.soils);
    let prevImpermeableLayerDepth = usePrevious(project.data!.slope?.impermeableLayerDepth);

    const getInputSoil = () =>
        project.data!.soilInputData!.soils!.map(x => {
            if (x.layerNumber == 1) {
                return getInputFirstLayerSoil(x);
            } else {
                return getInputNextLayerSoil(x);
            }
        });

    let input = getInputSoil();

    const formik = useFormik<IInputSoil[]>({
        initialValues: input,
        onSubmit: values => {},
    });

    React.useEffect(() => {
        if ((prevIso && iso !== prevIso) || (prevSoils && JSON.stringify(prevSoils) !== JSON.stringify(project.data!.soilInputData!.soils))) setValues(getInputSoil());
    }, [JSON.stringify(project.data!.soilInputData!.soils), iso]);

    React.useEffect(() => {
        if (prevImpermeableLayerDepth && JSON.stringify(prevImpermeableLayerDepth) !== JSON.stringify(project.data!.slope?.impermeableLayerDepth)) {
            let layers = [...formik.values];
            setValues(layers);
        }
    }, [JSON.stringify(project.data!.slope?.impermeableLayerDepth)]);

    const handleChange = (event: React.ChangeEvent<any> | SelectChangeEvent<any>, index: number) => {
        let { name, value, type, checked } = event.target;
        let isAvailableValue = true;
        if (type == 'checkbox') {
            value = checked;
        } else {
            value = parseFloat(value);
            isAvailableValue = !isNaN(value);
        }
        let updateValue = { [name]: value };
        if (isAvailableValue) {
            let layers = [...formik.values];
            layers[index] = {
                ...layers[index],
                ...updateValue,
            };
            setValues(layers, index);
            dispatch(cleanResult());
            dispatch(cleanImage());
            dispatch(cleanAlert());
        }
    };

    const setValues = (payload: IInputSoil[], index?: number) => {
        handleLayerThickness(payload, index);
        payload = handleHydraulicConductivity(payload);
        formik.setValues(payload);
        let soilInputData: ISoilInputData = {
            ...project.data?.soilInputData,
            layersInSeries: getLayersInSeries(payload),
            soils: payload.map(x => {
                let index = x.layerNumber! - 1;
                let soil = project.data?.soilInputData?.soils![index] ? project.data?.soilInputData?.soils?.[index] : { ...project.data?.soilInputData?.soils![0], layerNumber: x.layerNumber };
                return {
                    ...soil,
                    name: soil!.name,
                    soilPhysicalData: [{ ...soil!.soilPhysicalData![0], ...x }],
                };
            }),
        };
        dispatch(setSoilInputData({ ...project.data?.soilInputData, ...soilInputData }));
    };

    const handleHydraulicConductivity = (layers: IInputSoil[]) => {
        return layers.map(x => ({ ...x, hydraulicConductivity: getHydraulicConductivity(x.thickness!, x.drainedSoilPermeability! as number) }));
    };

    const handleLayerThickness = (layers: IInputSoil[], index?: number) => {
        let totalDepth = project.data?.slope?.impermeableLayerDepth!;
        let minThickness = project.data?.projectInfo.unitOfMeasure! == EUnitOfMeasure.Metric ? 0.5 : 1.6;
        if (layers.length == 1) {
            layers[0].thickness = totalDepth;
        } else {
            let firstLayerThickness = totalDepth;
            layers.forEach(x => {
                if (x.layerNumber != 1) firstLayerThickness -= x.thickness!;
            });
            if (firstLayerThickness <= minThickness) {
                dialog('warning', t('layerMaxThickness'));
                layers[0].thickness = minThickness;
                if (index == undefined) {
                    let availableThickness = totalDepth;
                    layers.forEach(x => {
                        if (x.layerNumber != layers.length) availableThickness -= x.thickness!;
                    });
                    layers[layers.length - 1].thickness = availableThickness;
                } else {
                    let availableThickness = totalDepth;
                    layers.forEach(x => {
                        if (x.layerNumber != index + 1) availableThickness -= x.thickness!;
                    });
                    layers[index].thickness = availableThickness;
                }
            } else {
                layers[0].thickness = firstLayerThickness;
            }
        }
    };

    const newSoilLayer = () => {
        dispatch(cleanResult());
        dispatch(cleanImage());
        dispatch(cleanAlert());

        let soil = [...formik.values];
        soil.push({ ...soil[0], thickness: 1, layerNumber: soil.length + 1 });
        setValues(soil);
    };

    const removeSoilLayer = () => {
        dispatch(cleanResult());
        dispatch(cleanImage());
        dispatch(cleanAlert());

        let soil = [...formik.values];
        soil.pop();
        setValues(soil);
    };

    const handleHidden = (name: string) => {
        if (name == layerNumber || name == hydraulicConductivity) return true;
        if (name == thickness && formik.values.length == 1) return true;
        if (name == remodeledSoilPermeability && !formik.values[0][permeabilityChangeUponRemodeling]) return true;
        return false;
    };

    const handleDisabled = (name: string, index: number) => {
        if (name == soilType) return true;
        if (name == thickness && index == 0) return true;
        return false;
    };

    const handleTextTooltip = (name: string) => {
        return undefined;
    };

    const handleClickSoilChoice = (index: number) => {
        setIndexSelected(index);
        handleOpenModal();
    };

    const handleCloseModal = () => {
        setIndexSelected(0);
        setOpenModal(false);
    };

    const handleOpenModal = () => setOpenModal(true);

    return (
        <>
            <Accordion disableGutters>
                <AccordionSummary variant='standard' expandIcon={<ExpandMoreIcon />} aria-controls='panel2a-content' id='panel2a-header'>
                    <Typography>{t('soilInput')}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    {formik.values.map(x => {
                        let index = x.layerNumber! - 1;
                        return (
                            <Stack key={`${x.soilType}_${index}`}>
                                <Stack variant='bottomLineTextGreen'>{t('soilData', { layer: x.layerNumber! })}</Stack>
                                <Button variant='green' style={{ width: '100%', margin: '0.5rem 0' }} onClick={() => handleClickSoilChoice(index)}>
                                    {t('soilChoice')}
                                </Button>
                                {Object.keys(x).map(y =>
                                    y == permeabilityChangeUponRemodeling ? (
                                        <Stack key={y}>
                                            <Switch
                                                textTooltip={t('permeabilityChangeUponRemodelingInfobox')}
                                                defaultChecked={formik.values[index][y as TInputSoil] as boolean}
                                                onChange={(e: any) => handleChange(e, index)}
                                                name={y}
                                                label={t(y)}
                                            />
                                        </Stack>
                                    ) : (
                                        <Stack key={y}>
                                            <Input
                                                hidden={handleHidden(y)}
                                                disabled={handleDisabled(y, index)}
                                                value={formik.values[index][y as TInputSoil]}
                                                minValue={inputRangeSoil[y]?.min.unitOfMeasure[project.data?.projectInfo.unitOfMeasure!]}
                                                maxValue={inputRangeSoil[y]?.max.unitOfMeasure[project.data?.projectInfo.unitOfMeasure!]}
                                                id={y}
                                                isNumber={typeof formik.values[index][y as TInputSoil] == 'number'}
                                                label={t(y, { unitOfMeasure: soilPD[y as TInputSoil]?.unitOfMeasure[project.data?.projectInfo.unitOfMeasure!] })}
                                                name={y}
                                                onChange={(e: any) => handleChange(e, index)}
                                                textTooltip={handleTextTooltip(y as TInputSoil)}
                                            />
                                        </Stack>
                                    ),
                                )}
                            </Stack>
                        );
                    })}
                    <Stack direction={'column'}>
                        <Stack direction={'row'} justifyContent={'space-between'} gap={1}>
                            <Button variant='green' style={{ width: formik.values.length > 1 ? '50%' : '100%', margin: '0.5rem 0' }} onClick={newSoilLayer}>
                                {t('addLayer')}
                            </Button>
                            {formik.values.length > 1 && (
                                <Button variant='orange' style={{ width: '50%', margin: '0.5rem 0' }} onClick={removeSoilLayer}>
                                    {t('deleteLayer')}
                                </Button>
                            )}
                        </Stack>
                        {formik.values.length > 1 && (
                            <Input
                                disabled
                                value={(project.data?.soilInputData?.layersInSeries! as number).toExponential(2)}
                                id={layersInSeries}
                                isNumber={true}
                                label={t(layersInSeries, { unitOfMeasure: soilInputDataPD[layersInSeries as TSoilInputData]?.unitOfMeasure[project.data?.projectInfo.unitOfMeasure!] })}
                                name={layersInSeries}
                            />
                        )}
                    </Stack>
                </AccordionDetails>
            </Accordion>
            <ModalSoil onClose={handleCloseModal} open={openModal} indexLayer={indexSelected} />
        </>
    );
};

export default InputSoil;
